import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CONTRIBUTORS, getContributorBySlug, matchContributorName } from '../data/contributors.js';
import { loadAllMarkdown } from '../utils/markdownLoader.js';
import Seo from '../components/Seo.jsx';
import { personSchema, profilePageSchema, breadcrumbSchema } from '../utils/schema.js';

// ─────────────────────────────────────────────────────────────────────────
// CONTRIBUTOR PAGE — /contributors/:slug
// Long-form bio + dictum + every article and blog post they've written.
// ─────────────────────────────────────────────────────────────────────────

// Lazy globs — fetched in parallel on mount. Per-file chunks are shared
// with the matching detail pages (ArticlePage, BlogPost, StatePage), so
// after visiting any detail page first, those chunks are already cached.
// Session 41 — articles glob points at enriched dir for cache sharing
// with ArticlePage (which now reads from enriched/).
const articleModules = import.meta.glob('../articles/enriched/*.md', {
  query: '?raw',
  import: 'default',
});

const blogModules = import.meta.glob('../blog/enriched/*.md', {
  query: '?raw',
  import: 'default',
});

const stateModules = import.meta.glob('../states/enriched/*.md', {
  query: '?raw',
  import: 'default',
});

// Light frontmatter parser — only what we need for listings.
function parseFrontmatter(raw) {
  const match = raw.match(/^---\s*\n([\s\S]*?)\n---/);
  if (!match) return {};
  const yamlText = match[1];
  const fm = {};
  let currentKey = null;
  for (const line of yamlText.split('\n')) {
    if (line.trim() === '' || line.trim().startsWith('#')) continue;
    const topMatch = line.match(/^([a-zA-Z][a-zA-Z0-9_]*):\s*(.*)$/);
    if (topMatch && !line.startsWith(' ')) {
      currentKey = topMatch[1];
      const value = topMatch[2].trim();
      if (value === '') {
        fm[currentKey] = null;
      } else if (value.startsWith('"') && value.endsWith('"')) {
        fm[currentKey] = value.slice(1, -1);
      } else {
        fm[currentKey] = value;
      }
      continue;
    }
    const subMatch = line.match(/^  ([a-zA-Z][a-zA-Z0-9_]*):\s*(.*)$/);
    if (subMatch) {
      const subKey = subMatch[1];
      const subValue = subMatch[2].trim();
      if (!fm[currentKey] || typeof fm[currentKey] !== 'object' || Array.isArray(fm[currentKey])) {
        fm[currentKey] = {};
      }
      fm[currentKey][subKey] = subValue.startsWith('"') && subValue.endsWith('"')
        ? subValue.slice(1, -1) : subValue;
    }
  }
  return fm;
}

const PILLAR_LABELS = {
  encyclopedia: 'ENCYCLOPEDIA',
  diagnostician: 'DIAGNOSTICIAN',
  'field-notes': 'FIELD NOTES',
  'pro-track': 'PRO TRACK',
};

const BLOG_CATEGORY_LABELS = {
  seasonal: 'Seasonal',
  industry: 'Industry',
  'how-to': 'How-to',
  opinion: 'Opinion',
  product: 'Product',
};

export default function ContributorPage() {
  const { slug } = useParams();
  const [dark, setDark] = useState(false);
  const [articles, setArticles] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [statePages, setStatePages] = useState([]);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  const contributor = getContributorBySlug(slug);

  // Find every article, blog post, and state page by this author. All three
  // globs load in parallel; per-file chunks come from the same micro-chunks
  // used by the matching detail routes, so re-visits are warm-cached.
  useEffect(() => {
    if (!contributor) {
      setArticles([]); setBlogPosts([]); setStatePages([]);
      return;
    }
    let cancelled = false;

    Promise.all([
      loadAllMarkdown(articleModules, parseFrontmatter),
      loadAllMarkdown(blogModules, parseFrontmatter),
      loadAllMarkdown(stateModules, parseFrontmatter),
    ]).then(([articleEntries, blogEntries, stateEntries]) => {
      if (cancelled) return;

      const matchedArticles = articleEntries
        .map((e) => e.frontmatter || {})
        .filter((fm) => {
          const match = matchContributorName(fm.author?.name);
          return match && match.slug === contributor.slug;
        })
        .sort((a, b) =>
          (b.publishedDate || '').localeCompare(a.publishedDate || '')
        );

      const matchedBlog = blogEntries
        .map((e) => e.frontmatter || {})
        .filter((fm) => {
          const match = matchContributorName(fm.author?.name);
          return match && match.slug === contributor.slug;
        })
        .sort((a, b) =>
          (b.publishedDate || '').localeCompare(a.publishedDate || '')
        );

      // Alphabetical by state name — easier to scan than a 20+ entry date
      // list, and contributors covering a region tend to ship adjacent
      // states close together so date sort doesn't add much.
      const matchedStates = stateEntries
        .map((e) => e.frontmatter || {})
        .filter((fm) => {
          const match = matchContributorName(fm.author?.name);
          return match && match.slug === contributor.slug;
        })
        .sort((a, b) =>
          (a.title || '').localeCompare(b.title || '')
        );

      setArticles(matchedArticles);
      setBlogPosts(matchedBlog);
      setStatePages(matchedStates);
    });

    return () => { cancelled = true; };
  }, [contributor]);

  const T = dark
    ? { bg: '#1A1D1C', surface: '#22262A', ink: '#F4F1EA',
        inkSoft: '#B8B2A4', inkFaint: '#6B6960', rule: '#2E3236',
        sienna: '#D67148', sage: '#9CA89A' }
    : { bg: '#F4F1EA', surface: '#FAF7F1', ink: '#1A1D1C',
        inkSoft: '#5A5A52', inkFaint: '#A39E91', rule: '#D9D3C5',
        sienna: '#B5532A', sage: '#9CA89A' };

  const fontDisplay = "'Fraunces', 'Times New Roman', serif";
  const fontBody = "'Inter Tight', sans-serif";
  const fontMono = "'JetBrains Mono', monospace";

  if (!contributor) {
    return (
      <div style={{
        minHeight: '100vh', background: T.bg, color: T.ink,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: fontDisplay, fontSize: 24, fontStyle: 'italic',
        padding: 40,
      }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: T.inkFaint, marginBottom: 24 }}>Contributor not found.</p>
          <Link to="/contributors" style={{
            color: T.sienna, textDecoration: 'none',
            fontFamily: fontMono, fontSize: 11, letterSpacing: '0.18em',
            fontStyle: 'normal',
          }}>
            ← BACK TO THE MASTHEAD
          </Link>
        </div>
      </div>
    );
  }

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso + 'T12:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).toUpperCase();
  };

  // Other contributors for the rail at the bottom
  const otherContributors = CONTRIBUTORS.filter((c) => c.slug !== contributor.slug);

  return (
    <div style={{
      minHeight: '100vh', background: T.bg, color: T.ink,
      fontFamily: fontBody, transition: 'background 0.3s, color 0.3s',
    }}>
      <Seo
        title={contributor?.name}
        description={
          contributor?.name
            ? `${contributor.name}${contributor.role ? ' — ' + contributor.role : ''}. Contributor at Window Washing Guide.`
            : undefined
        }
        path={`/contributors/${slug}`}
        ogType="profile"
        jsonLd={[
          personSchema(contributor),
          profilePageSchema(contributor),
          breadcrumbSchema([
            { name: 'Guide', path: '/' },
            { name: 'Contributors', path: '/contributors' },
            { name: contributor?.name || slug, path: `/contributors/${slug}` },
          ]),
        ]}
      />
      <style>{`
        .article-row, .blog-row { transition: background 0.18s, padding-left 0.18s; }
        .article-row:hover, .blog-row:hover { background: ${T.surface}; padding-left: 16px; }
        .ghost-link:hover { color: ${T.sienna}; }
        .other-contributor:hover { border-color: ${T.sienna} !important; }
        .other-contributor:hover .other-name { color: ${T.sienna}; }

        @media (max-width: 820px) {
          .container { padding: 24px 20px 60px !important; }
          .bio-h1 { font-size: 56px !important; }
          .others-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <div className="container" style={{ maxWidth: 880, margin: '0 auto', padding: '32px 28px 80px' }}>
        {/* Header */}
        <header style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          paddingBottom: 16, borderBottom: `1px solid ${T.rule}`, marginBottom: 32,
        }}>
          <Link to="/" style={{
            fontFamily: fontDisplay, fontSize: 22, letterSpacing: '-0.01em',
            color: T.ink, textDecoration: 'none',
          }}>
            Window Washing <em style={{ fontStyle: 'italic' }}>Guide</em>
          </Link>
          <button
            onClick={() => setDark(!dark)}
            style={{
              background: 'transparent', border: `1px solid ${T.rule}`,
              borderRadius: '50%', width: 32, height: 32,
              color: T.inkSoft, cursor: 'pointer', fontSize: 13,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {dark ? '☀' : '☾'}
          </button>
        </header>

        {/* Breadcrumb */}
        <div style={{
          fontFamily: fontMono, fontSize: 11,
          letterSpacing: '0.15em', color: T.inkFaint,
          marginBottom: 32,
        }}>
          <Link to="/contributors" style={{ color: T.inkFaint, textDecoration: 'none' }}>
            MASTHEAD
          </Link>
          {' / '}
          <span style={{ color: T.sienna }}>
            {contributor.name.toUpperCase()}
          </span>
        </div>

        {/* Role chip */}
        <div style={{
          fontFamily: fontMono, fontSize: 11,
          letterSpacing: '0.18em', color: T.sienna,
          marginBottom: 16,
        }}>
          {contributor.fullTitle.toUpperCase()}
        </div>

        {/* Name (massive) */}
        <h1 className="bio-h1" style={{
          fontFamily: fontDisplay, fontSize: 80, fontWeight: 400,
          lineHeight: 1.0, letterSpacing: '-0.03em',
          margin: '0 0 24px',
        }}>
          {contributor.name}
        </h1>

        {/* Stat rail */}
        <div style={{
          display: 'flex', gap: 32, flexWrap: 'wrap',
          paddingTop: 20, paddingBottom: 20,
          borderTop: `1px solid ${T.rule}`, borderBottom: `1px solid ${T.rule}`,
          marginBottom: 40,
        }}>
          <div>
            <div style={{
              fontFamily: fontMono, fontSize: 10,
              letterSpacing: '0.18em', color: T.inkFaint,
              marginBottom: 4,
            }}>
              BASED IN
            </div>
            <div style={{
              fontFamily: fontDisplay, fontSize: 18, color: T.ink,
            }}>
              {contributor.location}
            </div>
          </div>
          <div>
            <div style={{
              fontFamily: fontMono, fontSize: 10,
              letterSpacing: '0.18em', color: T.inkFaint,
              marginBottom: 4,
            }}>
              EXPERIENCE
            </div>
            <div style={{
              fontFamily: fontDisplay, fontSize: 18, color: T.ink,
            }}>
              {contributor.tenure}
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{
              fontFamily: fontMono, fontSize: 10,
              letterSpacing: '0.18em', color: T.inkFaint,
              marginBottom: 8,
            }}>
              BEATS
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {contributor.beats.map((beat) => (
                <span key={beat} style={{
                  fontFamily: fontMono, fontSize: 10,
                  letterSpacing: '0.1em', color: T.inkSoft,
                  border: `1px solid ${T.rule}`,
                  padding: '4px 10px',
                }}>
                  {beat.toUpperCase()}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Dictum */}
        {contributor.dictum && (
          <div style={{
            background: T.surface,
            borderLeft: `2px solid ${T.sienna}`,
            padding: '24px 28px',
            marginBottom: 48,
          }}>
            <div style={{
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.18em', color: T.sienna,
              marginBottom: 12,
            }}>
              THE LINE THEY KEEP REPEATING
            </div>
            <p style={{
              fontFamily: fontDisplay, fontSize: 24, fontStyle: 'italic',
              fontWeight: 400, lineHeight: 1.35, color: T.ink,
              margin: '0 0 12px',
            }}>
              "{contributor.dictum}"
            </p>
            {contributor.dictumAttribution && (
              <p style={{
                fontFamily: fontBody, fontSize: 13,
                color: T.inkSoft, lineHeight: 1.5, margin: 0,
                fontStyle: 'italic',
              }}>
                — {contributor.dictumAttribution}
              </p>
            )}
          </div>
        )}

        {/* Long bio */}
        <div style={{ marginBottom: 56 }}>
          <div style={{
            fontFamily: fontMono, fontSize: 11,
            letterSpacing: '0.18em', color: T.sienna,
            marginBottom: 20,
          }}>
            ABOUT {contributor.name.split(' ')[0].toUpperCase()}
          </div>
          {contributor.longBio.map((para, i) => (
            <p key={i} style={{
              fontFamily: fontDisplay, fontSize: 18,
              lineHeight: 1.7, color: T.ink,
              margin: '0 0 22px',
            }}>
              {para}
            </p>
          ))}
        </div>

        {/* Articles */}
        {articles.length > 0 && (
          <section style={{ marginBottom: 56 }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
              marginBottom: 20,
            }}>
              <div style={{
                fontFamily: fontMono, fontSize: 11,
                letterSpacing: '0.18em', color: T.sienna,
              }}>
                § ARTICLES BY {contributor.name.split(' ')[0].toUpperCase()}
              </div>
              <span style={{
                fontFamily: fontMono, fontSize: 11,
                letterSpacing: '0.15em', color: T.inkFaint,
              }}>
                {articles.length} {articles.length === 1 ? 'PIECE' : 'PIECES'}
              </span>
            </div>

            <div style={{ borderTop: `1px solid ${T.rule}` }}>
              {articles.map((a) => (
                <Link
                  key={a.slug}
                  to={`/articles/${a.slug}`}
                  className="article-row"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '110px 1fr 32px',
                    gap: 20, alignItems: 'center',
                    padding: '20px 0',
                    borderBottom: `1px solid ${T.rule}`,
                    textDecoration: 'none', color: T.ink,
                  }}
                >
                  <div style={{
                    fontFamily: fontMono, fontSize: 10,
                    letterSpacing: '0.15em', color: T.sienna,
                  }}>
                    {PILLAR_LABELS[a.pillar] || (a.pillarLabel || '').toUpperCase() || '—'}
                  </div>
                  <div>
                    <div style={{
                      fontFamily: fontDisplay, fontSize: 19,
                      lineHeight: 1.3, marginBottom: 4,
                    }}>
                      {a.title}
                    </div>
                    {a.publishedDate && (
                      <div style={{
                        fontFamily: fontMono, fontSize: 10,
                        letterSpacing: '0.12em', color: T.inkFaint,
                      }}>
                        {formatDate(a.publishedDate)}
                        {a.readTime && ` · ${a.readTime.toUpperCase()}`}
                      </div>
                    )}
                  </div>
                  <span style={{ color: T.inkFaint, textAlign: 'right' }}>→</span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Blog posts */}
        {blogPosts.length > 0 && (
          <section style={{ marginBottom: 56 }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
              marginBottom: 20,
            }}>
              <div style={{
                fontFamily: fontMono, fontSize: 11,
                letterSpacing: '0.18em', color: T.sienna,
              }}>
                § BLOG POSTS BY {contributor.name.split(' ')[0].toUpperCase()}
              </div>
              <span style={{
                fontFamily: fontMono, fontSize: 11,
                letterSpacing: '0.15em', color: T.inkFaint,
              }}>
                {blogPosts.length} {blogPosts.length === 1 ? 'POST' : 'POSTS'}
              </span>
            </div>

            <div style={{ borderTop: `1px solid ${T.rule}` }}>
              {blogPosts.map((b) => (
                <Link
                  key={b.slug}
                  to={`/blog/${b.slug}`}
                  className="blog-row"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '110px 1fr 32px',
                    gap: 20, alignItems: 'center',
                    padding: '20px 0',
                    borderBottom: `1px solid ${T.rule}`,
                    textDecoration: 'none', color: T.ink,
                  }}
                >
                  <div style={{
                    fontFamily: fontMono, fontSize: 10,
                    letterSpacing: '0.15em', color: T.sienna,
                  }}>
                    {(BLOG_CATEGORY_LABELS[b.category] || 'POST').toUpperCase()}
                  </div>
                  <div>
                    <div style={{
                      fontFamily: fontDisplay, fontSize: 19,
                      lineHeight: 1.3, marginBottom: 4,
                    }}>
                      {b.title}
                    </div>
                    {b.publishedDate && (
                      <div style={{
                        fontFamily: fontMono, fontSize: 10,
                        letterSpacing: '0.12em', color: T.inkFaint,
                      }}>
                        {formatDate(b.publishedDate)}
                        {b.readTime && ` · ${b.readTime.toUpperCase()}`}
                      </div>
                    )}
                  </div>
                  <span style={{ color: T.inkFaint, textAlign: 'right' }}>→</span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* State pages */}
        {statePages.length > 0 && (
          <section style={{ marginBottom: 56 }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
              marginBottom: 20,
            }}>
              <div style={{
                fontFamily: fontMono, fontSize: 11,
                letterSpacing: '0.18em', color: T.sienna,
              }}>
                § STATE PAGES BY {contributor.name.split(' ')[0].toUpperCase()}
              </div>
              <span style={{
                fontFamily: fontMono, fontSize: 11,
                letterSpacing: '0.15em', color: T.inkFaint,
              }}>
                {statePages.length} {statePages.length === 1 ? 'STATE' : 'STATES'}
              </span>
            </div>

            <div style={{ borderTop: `1px solid ${T.rule}` }}>
              {statePages.map((s) => (
                <Link
                  key={s.slug}
                  to={`/states/${s.slug}`}
                  className="article-row"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '110px 1fr 32px',
                    gap: 20, alignItems: 'center',
                    padding: '20px 0',
                    borderBottom: `1px solid ${T.rule}`,
                    textDecoration: 'none', color: T.ink,
                  }}
                >
                  <div style={{
                    fontFamily: fontMono, fontSize: 10,
                    letterSpacing: '0.15em', color: T.sienna,
                  }}>
                    STATE PAGE
                  </div>
                  <div>
                    <div style={{
                      fontFamily: fontDisplay, fontSize: 19,
                      lineHeight: 1.3, marginBottom: 4,
                    }}>
                      {s.title}
                    </div>
                    {(s.publishedDate || s.readTime) && (
                      <div style={{
                        fontFamily: fontMono, fontSize: 10,
                        letterSpacing: '0.12em', color: T.inkFaint,
                      }}>
                        {s.publishedDate && formatDate(s.publishedDate)}
                        {s.readTime && ` · ${s.readTime.toUpperCase()}`}
                      </div>
                    )}
                  </div>
                  <span style={{ color: T.inkFaint, textAlign: 'right' }}>→</span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Empty state — shouldn't normally trigger but guards new contributors */}
        {articles.length === 0 && blogPosts.length === 0 && statePages.length === 0 && (
          <p style={{
            fontFamily: fontDisplay, fontSize: 18, fontStyle: 'italic',
            color: T.inkSoft, padding: '24px 0', marginBottom: 56,
          }}>
            No published pieces yet under this byline. Check back soon.
          </p>
        )}

        {/* Other contributors */}
        <section style={{
          paddingTop: 32, marginTop: 8,
          borderTop: `1px solid ${T.rule}`,
        }}>
          <div style={{
            fontFamily: fontMono, fontSize: 11,
            letterSpacing: '0.18em', color: T.sienna,
            marginBottom: 20,
          }}>
            OTHER VOICES
          </div>
          <div className="others-grid" style={{
            display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 12,
          }}>
            {otherContributors.map((c) => (
              <Link
                key={c.slug}
                to={`/contributors/${c.slug}`}
                className="other-contributor"
                style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  padding: '14px 16px',
                  border: `1px solid ${T.rule}`,
                  textDecoration: 'none', color: T.ink,
                  transition: 'border-color 0.18s',
                }}
              >
                <div style={{
                  width: 36, height: 36, borderRadius: '50%',
                  background: T.surface, border: `1px solid ${T.rule}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: fontDisplay, fontSize: 18, color: T.inkSoft,
                  flexShrink: 0,
                }}>
                  {c.initial}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="other-name" style={{
                    fontFamily: fontDisplay, fontSize: 16, fontWeight: 500,
                    color: T.ink, transition: 'color 0.18s',
                  }}>
                    {c.name}
                  </div>
                  <div style={{
                    fontFamily: fontMono, fontSize: 9,
                    letterSpacing: '0.15em', color: T.inkFaint,
                    marginTop: 2,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {c.fullTitle.toUpperCase()}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Footer */}
        <footer style={{
          marginTop: 56, paddingTop: 24,
          borderTop: `1px solid ${T.rule}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        }}>
          <Link to="/contributors" className="ghost-link" style={{
            fontFamily: fontMono, fontSize: 11,
            letterSpacing: '0.18em', color: T.sienna,
            textDecoration: 'none',
          }}>
            ← BACK TO THE MASTHEAD
          </Link>
          <Link to="/" className="ghost-link" style={{
            fontFamily: fontMono, fontSize: 11,
            letterSpacing: '0.18em', color: T.inkSoft,
            textDecoration: 'none',
          }}>
            HOMEPAGE →
          </Link>
        </footer>
      </div>
    </div>
  );
}
